import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	Output,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { MatDatepicker } from "@angular/material/datepicker";
import { MatLegacyMenuTrigger as MatMenuTrigger } from "@angular/material/legacy-menu";
import {
	DepositLimitCalculationError,
	DepositLimitCalculatorMethods,
	DepositLimitCalculatorPredictionsQuery,
} from "@obg/ngx.common";
import { coerceBoolean } from "@obg/ngx.global.evo";

@Component({
	selector: "obg-m-deposit-limit-calculator-form",
	templateUrl: "./deposit-limit-calculator-form.component.html",
	styleUrls: ["./deposit-limit-calculator-form.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class DepositLimitCalculatorFormComponent {
	@HostBinding("class.obg-m-deposit-limit-calculator-form") class = true;

	@Output() calculate = new EventEmitter<DepositLimitCalculatorPredictionsQuery>();
	@Output() inputChange = new EventEmitter<string>();
	@Output() updateStartDate = new EventEmitter<void>();

	@ViewChild(MatMenuTrigger, { static: true }) menuTrigger: MatMenuTrigger | undefined;
	@ViewChild("picker") datePicker: MatDatepicker<Date> | undefined;

	@Input() @coerceBoolean() busy = false;
	@Input() calculationError?: DepositLimitCalculationError = undefined;

	@Input() formGroup: UntypedFormGroup | undefined;
	@Input() currencyCode = "";
	@Input() hoursOfTheDay: string[] | undefined;
	@Input() amountMaxValidator: number | undefined;
	@Input() startDate: Date | undefined;

	displayErrorsDate = false;
	displayErrorsTime = false;

	onDatePickerClick(): void {
		this.datePicker?.open();
		this.updateStartDate.emit();
	}

	onDatePickerClosed(): void {
		const selectedTime = this.formGroup?.controls[DepositLimitCalculatorMethods.Time].value as string;
		const isSelectedTimeAfterNow = this.hoursOfTheDay?.includes(
			this.formGroup?.controls[DepositLimitCalculatorMethods.Time].value
		);

		// Reset the time if it has already passed, otherwise do nothing with it.
		if (selectedTime && !isSelectedTimeAfterNow) {
			this.formGroup?.controls[DepositLimitCalculatorMethods.Time].reset();
		}

		this.displayErrorsDate = true;
		this.onInputChange("date");
	}

	onMenuClick(): void {
		this.menuTrigger?.openMenu();
	}

	onTimeMenuClosed(): void {
		this.displayErrorsTime = true;
	}

	onSelectTime(time: string): void {
		this.onInputChange("time");
		this.formGroup?.controls.time.setValue(time);
	}

	onInputChange(controlName: string): void {
		// We need to emit when the input change occurs, not in the input event, so that the GTM event doesn't get pushed multiple times
		this.inputChange.emit(controlName);
	}

	onAmountInputChange(): void {
		this.onInputChange("amount");
	}

	onSubmit() {
		if (!this.formGroup?.valid) {
			return;
		}
		const type = this.formGroup?.controls.amount
			? DepositLimitCalculatorMethods.Amount
			: DepositLimitCalculatorMethods.Date;
		const value =
			type === DepositLimitCalculatorMethods.Amount
				? { amount: this.formGroup?.controls[DepositLimitCalculatorMethods.Amount].value }
				: {
						date: this.formGroup?.controls[DepositLimitCalculatorMethods.Date].value,
						time: this.formGroup?.controls[DepositLimitCalculatorMethods.Time].value,
					};
		this.calculate.emit({ value, type });
	}
}
